import type React from 'react';
import {
  useHandleScreeningReports,
  useIdVerificationCallbacks,
  usePidAnswers,
  useReauthenticating,
  useTermSet,
} from '../../../IdvContext';
import { Box, Heading, Icon, Paragraph, Text } from '@zillow/constellation-10';
import { IconWarningFilled } from '@zillow/constellation-icons';
import { ExitReason, TermSet } from '../../../../../models';
import { LANDLORD_CLICKSTREAM_EVENTS } from '../../../clickstream/events';
import { formatPhoneNumber } from '../../../format/format';
import { useEffectOnce } from 'react-use';
import { useCallback, useContext, useEffect } from 'react';
import usePerformIdentityCheck from '../../../hooks/usePerformIdentityCheck';
import { WizardContext } from '../../../wizard/WizardContext';

const EMPTY_FIELD_TEXT = 'Not provided';

function Dt({ children }: { children: React.ReactNode }) {
  return (
    <Text asChild textStyle="body-sm-bold">
      <dt>{children}</dt>
    </Text>
  );
}

function Dd({ children, ...props }: { children: React.ReactNode; css?: React.ComponentProps<typeof Text>['css'] }) {
  return (
    <Text asChild {...props}>
      <dd>{children}</dd>
    </Text>
  );
}

/* eslint-disable curly-quotes/no-straight-quotes */
const renterTemplateArea = `
  ". head"
  ". subHead"
  ". firstName"
  "middleNameWarn middleName"
  ". lastName"
  ". dob"
  ". phone"
  "ssnWarn ssn"
  ". address"
`;

const landlordTemplateArea = `
  ". head"
  ". subHead"
  ". firstName"
  "middleNameWarn middleName"
  ". lastName"
  "ssnWarn ssn"
  ". address"
`;
/* eslint-enable curly-quotes/no-straight-quotes */

export default function ReviewStep() {
  const pidAnswers = usePidAnswers();
  const { firstName, middleName, lastName, currentAddress, currentCity, currentState, currentZip, dob, phone, ssn } =
    pidAnswers;
  const termSet = useTermSet();
  const handleScreeningReports = useHandleScreeningReports();
  const { trackEvent, onExit } = useIdVerificationCallbacks();
  const reauthenticating = useReauthenticating();
  const { setOnNext } = useContext(WizardContext);

  useEffectOnce(() => {
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.viewPage('id_verification_review_personal_information'));
  });

  const performIdentityCheck = usePerformIdentityCheck();

  const handleSubmit = useCallback(async () => {
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNext('id_verification_review_personal_information'));
    if (!ssn && reauthenticating) {
      await onExit({ reason: ExitReason.AttemptedReAuthWithoutSsn, success: false });
      return;
    }

    performIdentityCheck();
  }, [trackEvent, ssn, reauthenticating, onExit, performIdentityCheck]);

  useEffect(() => {
    setOnNext(() => handleSubmit);
    return () => setOnNext(null);
  }, [handleSubmit, setOnNext]);

  return (
    <Box
      asChild
      css={{
        display: 'grid',
        rowGap: 'default',
        gridTemplateColumns: 'max-content 1fr',
        gridTemplateAreas: termSet === TermSet.RenterScreening ? renterTemplateArea : landlordTemplateArea,
      }}
    >
      <dl>
        <Box css={{ gridArea: 'head' }}>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
            Review your personal information
          </Heading>
        </Box>
        <Box css={{ gridArea: 'subHead' }}>
          <Paragraph css={{ marginBottom: 'default' }}>
            {termSet === TermSet.RenterScreening
              ? 'The information you previously entered will be sent to Experian to verify your identity. If it’s incorrect, you may not be able to submit the application.'
              : 'The information you previously entered will be used to verify your identity. If it’s incorrect, you may not be able to review your applicants’ information.'}
          </Paragraph>
        </Box>
        <Box css={{ gridArea: 'firstName', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <Dt>Legal first name</Dt>
          <Dd>{firstName}</Dd>
        </Box>
        <Box css={{ gridArea: 'middleNameWarn', marginRight: 'tighter' }}>
          {!middleName ? (
            <Icon css={{ marginTop: 'tightest', color: 'icon.feedback.warning.hero' }}>
              <IconWarningFilled />
            </Icon>
          ) : null}
        </Box>
        <Box css={{ gridArea: 'middleName', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <Dt>Legal middle name</Dt>
          <Dd css={{ color: middleName ? 'text.neutral' : 'text.subtle' }}>
            {middleName ? middleName : EMPTY_FIELD_TEXT}
          </Dd>
          {!middleName && (
            <Paragraph textStyle="fineprint" css={{ color: 'text.feedback.warning.hero' }}>
              If you have a middle name, you need to add it so that your identity can be verified
            </Paragraph>
          )}
        </Box>
        <Box css={{ gridArea: 'lastName', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <Dt>Legal last name</Dt>
          <Dd>{lastName}</Dd>
        </Box>
        {termSet === TermSet.RenterScreening && (
          <>
            <Box css={{ gridArea: 'dob', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Dt>Date of birth</Dt>
              <Dd>{dob}</Dd>
            </Box>
            <Box css={{ gridArea: 'phone', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Dt>Phone number</Dt>
              <Dd>{formatPhoneNumber(phone)}</Dd>
            </Box>
          </>
        )}
        <Box css={{ gridArea: 'ssnWarn', marginRight: 'tighter' }}>
          {!ssn && handleScreeningReports ? (
            <Icon css={{ marginTop: 'tightest', color: 'icon.feedback.warning.hero' }}>
              <IconWarningFilled />
            </Icon>
          ) : null}
        </Box>
        <Box css={{ gridArea: 'ssn', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <Dt>{termSet === TermSet.RenterScreening ? 'SSN or ITIN' : 'Social Security number'}</Dt>
          {ssn ? (
            <Dd>XXX-XX-XXXX</Dd>
          ) : (
            <>
              <Paragraph css={{ color: 'text.subtle' }}>{EMPTY_FIELD_TEXT}</Paragraph>
              {handleScreeningReports && (
                <Paragraph textStyle="fineprint" css={{ color: 'text.feedback.warning.hero' }}>
                  {termSet === TermSet.RenterScreening
                    ? 'Add your SSN or ITIN to accurately verify your identity and pull the correct screening reports'
                    : 'If you have a Social Security number, you need to add it so that your identity can be verified'}
                </Paragraph>
              )}
            </>
          )}
        </Box>
        <Box css={{ gridArea: 'address', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <Dt>Current address</Dt>
          {/* We use a <br/> instead of 2 paragraphs because 'dd' and 'dt' tags should alternate with each other */}
          <Dd>
            {currentAddress}
            <br />
            {`${currentCity}, ${currentState} ${currentZip}`}
          </Dd>
        </Box>
      </dl>
    </Box>
  );
}
